import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Format date
export const formatDate = (date: Date | string, format: 'short' | 'long' | 'relative' = 'short') => {
  const d = new Date(date);
  
  if (format === 'relative') {
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (seconds < 60) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    if (days < 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  }
  
  if (format === 'long') {
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format currency
export const formatCurrency = (amount: number, currency: string = 'INR') => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// Calculate age from date of birth
export const calculateAge = (dob: Date | string) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

// Format height (cm to feet-inches)
export const formatHeight = (heightInCm: number) => {
  const totalInches = heightInCm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);
  return `${feet}'${inches}"`;
};

// Truncate text
export const truncateText = (text: string, maxLength: number = 100) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

// Get initials from name
export const getInitials = (firstName: string, lastName?: string) => {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last;
};

// Validate email
export const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone
export const isValidPhone = (phone: string) => {
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(phone);
};

// Format phone number
export const formatPhone = (phone: string) => {
  if (phone.length === 10) {
    return `+91 ${phone.substring(0, 5)} ${phone.substring(5)}`;
  }
  return phone;
};

// Generate random color
export const generateColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = Math.floor(Math.abs((Math.sin(hash) * 16777215) % 1) * 16777215).toString(16);
  return '#' + '0'.repeat(6 - color.length) + color;
};

// Debounce function
export function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle function
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Copy to clipboard
export const copyToClipboard = async (text: string) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy:', err);
    return false;
  }
};

// Download file
export const downloadFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Check if user is online (last active within 5 minutes)
export const isOnline = (lastActive: Date | string) => {
  const now = new Date();
  const lastActiveDate = new Date(lastActive);
  const diff = now.getTime() - lastActiveDate.getTime();
  return diff < 5 * 60 * 1000; // 5 minutes
};

// Get file size in readable format
export const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Validate image file
export const isValidImage = (file: File) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 5 * 1024 * 1024; // 5MB
  
  if (!validTypes.includes(file.type)) {
    return { valid: false, error: 'Please upload a valid image file (JPEG, PNG, GIF, or WebP)' };
  }
  
  if (file.size > maxSize) {
    return { valid: false, error: 'Image size must be less than 5MB' };
  }
  
  return { valid: true };
};

// Convert file to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

// Get reading time from text
export const getReadingTime = (text: string) => {
  const wordsPerMinute = 200;
  const words = text.trim().split(/\s+/).length;
  const time = Math.ceil(words / wordsPerMinute);
  return time;
};

// Strip HTML tags
export const stripHtml = (html: string) => {
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// Generate slug from text
export const generateSlug = (text: string) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Check if subscription is active
export const isSubscriptionActive = (subscription: any) => {
  if (!subscription || !subscription.isActive) return false;
  if (!subscription.endDate) return false;
  const endDate = new Date(subscription.endDate);
  return endDate > new Date();
};

// Get subscription status
export const getSubscriptionStatus = (subscription: any) => {
  if (!subscription || !subscription.planId) return 'none';
  if (!subscription.isActive) return 'expired';
  if (!subscription.endDate) return 'active';
  
  const endDate = new Date(subscription.endDate);
  const now = new Date();
  const daysLeft = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  
  if (daysLeft <= 0) return 'expired';
  if (daysLeft <= 7) return 'expiring';
  return 'active';
};

// Parse error message from API response
export const parseErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.message) return error.message;
  return 'An error occurred. Please try again.';
};

// Sanitize HTML for safe rendering
export const sanitizeHtml = (html: string) => {
  const temp = document.createElement('div');
  temp.textContent = html;
  return temp.innerHTML;
};

// Calculate match percentage (basic implementation)
export const calculateMatchPercentage = (userPreferences: any, profile: any) => {
  let score = 0;
  let total = 0;
  
  // Age match
  if (userPreferences.ageRange && profile.age) {
    total++;
    if (profile.age >= userPreferences.ageRange.min && profile.age <= userPreferences.ageRange.max) {
      score++;
    }
  }
  
  // Height match
  if (userPreferences.heightRange && profile.height) {
    total++;
    if (profile.height >= userPreferences.heightRange.min && profile.height <= userPreferences.heightRange.max) {
      score++;
    }
  }
  
  // Religion match
  if (userPreferences.religion && profile.religion) {
    total++;
    if (userPreferences.religion.includes(profile.religion)) {
      score++;
    }
  }
  
  // Education match
  if (userPreferences.education && profile.education?.level) {
    total++;
    if (userPreferences.education.includes(profile.education.level)) {
      score++;
    }
  }
  
  // Location match
  if (userPreferences.city && profile.city) {
    total++;
    if (userPreferences.city.includes(profile.city)) {
      score++;
    }
  }
  
  return total > 0 ? Math.round((score / total) * 100) : 0;
};

// Format commission amount
export const formatCommission = (amount: number, percentage: number = 50) => {
  const commission = (amount * percentage) / 100;
  return {
    commission,
    netAmount: amount - commission,
    percentage
  };
};